import React from 'react';
import getProp from 'lodash/get';
import validator from 'validator';
import Field from '../layout/Field.jsx';
import ContentContainer from '../layout/ContentContainer.jsx';
import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import { getTextDefaults } from './translations/contactInformationSectionTranslations.js';

const ContactInformationSection = ({
	Registry,
	name = 'contacts',
	fields = [],
	values: formValues = {},
	title,
	errors,
	onChange,
	navigationProps,
	selectedLanguage = 'EN',
	disabled = false,
	textOverride = {},
}) => {
	// Get translations
	const textDefaults = getTextDefaults(selectedLanguage);
	const text = { ...textDefaults, ...textOverride };
	// Extract values scoped under the section name
	const values = formValues || {};

	const areRequiredFieldsCompleted = () => {
		return fields.every(field => {
			if (field.type !== 'ContactInformationGroup' || !field.required) {
				return true;
			}

			const fieldValue = getProp(values, field.name);
			if (!fieldValue?.name || !fieldValue?.phone?.trim()) {
				return false;
			}
			if (fieldValue?.email && fieldValue.email.trim() !== '') {
				return validator.isEmail(fieldValue.email);
			}
			return true;
		});
	};

	const canContinue = areRequiredFieldsCompleted();

	// Prefix field names with section name
	const handleChange = ({ target }) => {
		onChange({ target: { ...target, name: target.name }});
	};
	
	// Override next function to call submit when it's the last section
	// This mimics how CompanySection overrides next on its finished screen
	const handleNext = (event) => {
		if (!navigationProps.hasNext && navigationProps.submit) {
			// Last section - call submit directly
			return navigationProps.submit(event);
		}
		// Not last section - call next
		return navigationProps.next(event);
	};

	const Fields = fields.map((field, i) =>
		<Field
			Registry={Registry}
			{...field}
			name={`${field.name}`}
			key={`${field.name}-${i}`}
			onChange={handleChange}
			values={formValues}
			errors={errors}
			disabled={disabled}
			nameLabel={text.nameLabel}
			emailLabel={text.emailLabel}
			phoneLabel={text.phoneLabel}
			namePlaceholder={text.namePlaceholder}
			emailPlaceholder={text.emailPlaceholder}
			invalidEmailText={text.invalidEmailText}
		/>);

	return (
		<SectionContainer
			Navigation={
				<SectionNav
					{...navigationProps}
					canContinue={canContinue}
					next={handleNext}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			<ContentContainer>
				{title && <h2 className="nf-section-title" key={title.replace(' ', '-')}>{title}</h2>}
				{Fields}
			</ContentContainer>
		</SectionContainer>
	);
};

ContactInformationSection.type = 'ContactInformationSection';
ContactInformationSection.hasNavigation = true;
ContactInformationSection.spec = [{
	name: 'ContactInformationSection',
	key: 'ContactInformationSection',
	title: 'Contact Information',
	allowedFields: ['ContactInformationGroup'],
	translatable: ['title', 'navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
	fields: [{
		type: 'TextField',
		name: 'title',
		label: 'Section title',
	}, {
		type: 'TextField',
		name: 'navigation.previousLabel',
		label: 'Previous label',
		helperText: 'if there is a previous section, this will be the label to go back.',
	}, {
		type: 'TextField',
		name: 'navigation.nextLabel',
		label: 'Next label',
		helperText: 'if there is a next section, this will be the label to go forward.',
	}, {
		type: 'TextField',
		name: 'navigation.submitLabel',
		label: 'Submit label',
		helperText: 'If this section is the last section, this will be the text on the submit button.',
	}],
}];


export default ContactInformationSection;
